import type { LucideIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { EventType, ScheduleSpec } from '../../contracts/routines.types';
import { formatEventTriggerSummary } from '../../contracts/workflows/lib/build-trigger-line';
import {
  isCreateRoutineTool,
  isEventTriggerRoutineTool,
  isScheduleRoutineTool,
} from '../../contracts/workflows/lib/routineToolNames';
import {
  useWorkflow,
  useWorkflowEventRules,
  useWorkflowSchedule,
} from '../../data/query/useQueryRoutines';

import type { ApprovalRequestInput } from '../projects/components/ApprovalRequestBlock.display';

import {
  useRoutineApprovalResolve,
  type RoutineApprovalResolve,
} from './routine-approval-resolve';
import { ROUTINE_EVENT_TYPE_LABELS } from './routine-event-trigger-labels';
import type { RoutineProposalKind } from './routine-proposal-card';
import { RoutineProposalCard, type RoutineProposalDiff } from './routine-proposal-card';
import {
  formatRoutineScheduleLabel,
  formatRoutineScheduleSentence,
} from './routine-schedule-copy';

export type { RoutineApprovalResolve } from './routine-approval-resolve';

export interface RoutineApprovalCardProps {
  approval: ApprovalRequestInput;
  onResolve: RoutineApprovalResolve;
  Icon: LucideIcon;
  /** App-composed read gate (workflows flag + auth) — matches `@/features/workflows/hooks`. */
  routinesReadEnabled: boolean;
}

function isScheduleSpec(value: unknown): value is ScheduleSpec {
  if (!value || typeof value !== 'object') return false;
  const spec = value as Partial<ScheduleSpec>;
  return (
    typeof spec.start_at === 'string' &&
    (spec.timezone === undefined || typeof spec.timezone === 'string') &&
    !!spec.frequency &&
    typeof spec.frequency === 'object' &&
    typeof (spec.frequency as { kind?: unknown }).kind === 'string'
  );
}

function cadenceKey(spec: ScheduleSpec | undefined): string {
  if (!spec) return '';
  const frequency =
    spec.frequency.kind === 'cron'
      ? `cron:${spec.frequency.cron_expression}`
      : spec.frequency.kind;
  return `${frequency}|${spec.start_at}|${spec.timezone}`;
}

function sortedEventTypes(values: string[] | undefined): EventType[] {
  return [...(values ?? [])].filter(Boolean).sort() as EventType[];
}

function readRoutineCreateFields(toolInput: Record<string, unknown>): {
  name: string;
  instructions: string;
} {
  return {
    name: typeof toolInput.name === 'string' ? toolInput.name : '',
    instructions:
      typeof toolInput.instructions === 'string' ? toolInput.instructions : '',
  };
}

export const RoutineApprovalCard = ({
  approval,
  onResolve,
  Icon,
  routinesReadEnabled,
}: RoutineApprovalCardProps): JSX.Element => {
  const intl = useIntl();
  const toolName = approval.tool_name;
  const input = approval.tool_input;
  const workflowId =
    typeof input.workflow_id === 'string' ? input.workflow_id : undefined;
  const kind: RoutineProposalKind = isCreateRoutineTool(toolName) ? 'create' : 'update';
  const { submit, pendingVerdict, isBusy } = useRoutineApprovalResolve(
    approval.approval_id,
    onResolve,
    workflowId
  );

  const needsCurrent = kind === 'update';
  const workflowQuery = useWorkflow({
    workflowId: needsCurrent ? workflowId : undefined,
    enabled: routinesReadEnabled,
  });
  const scheduleQuery = useWorkflowSchedule({
    workflowId: needsCurrent ? workflowId : undefined,
    enabled: routinesReadEnabled,
  });
  const rulesQuery = useWorkflowEventRules({
    workflowId: needsCurrent ? workflowId : undefined,
    enabled: routinesReadEnabled,
  });
  const currentLoading =
    needsCurrent &&
    (workflowQuery.isLoading || scheduleQuery.isLoading || rulesQuery.isLoading);
  const currentReady =
    !needsCurrent ||
    (Boolean(workflowId) &&
      !currentLoading &&
      !workflowQuery.isError &&
      !scheduleQuery.isError &&
      !rulesQuery.isError);

  const createFields = useMemo(
    () => (kind === 'create' ? readRoutineCreateFields(input) : null),
    [kind, input]
  );

  const noneLabel = intl.formatMessage({
    defaultMessage: 'None',
    id: 'dyYjX+/vF2',
    description: 'Placeholder when a routine field is empty in a before/after diff',
  });

  const formatTriggers = (types: EventType[]): string => {
    if (types.length === 0) return noneLabel;
    return types
      .map((eventType) =>
        ROUTINE_EVENT_TYPE_LABELS[eventType]
          ? intl.formatMessage(ROUTINE_EVENT_TYPE_LABELS[eventType])
          : eventType
      )
      .join('\n');
  };

  const name =
    kind === 'create'
      ? (createFields?.name ?? '')
      : (workflowQuery.data?.name ?? (typeof input.name === 'string' ? input.name : ''));

  const proposedSpec = isScheduleSpec(input.spec) ? input.spec : undefined;
  const currentSpec = scheduleQuery.data?.spec;
  const proposedEventTypes = sortedEventTypes(
    Array.isArray(input.event_types) ? (input.event_types as string[]) : undefined
  );
  const currentEventTypes = sortedEventTypes(
    (rulesQuery.data?.event_rules ?? [])
      .filter((rule) => rule.enabled)
      .map((rule) => rule.event_type)
  );

  const diffs: RoutineProposalDiff[] = [];
  let summary: string | undefined;

  const runsManually = intl.formatMessage({
    defaultMessage: 'Runs manually',
    id: 'pRWVWqmcqy',
    description: 'Subtitle when a new routine has no schedule and no event triggers',
  });

  if (kind === 'create') {
    summary = proposedSpec
      ? formatRoutineScheduleSentence(intl, proposedSpec)
      : (formatEventTriggerSummary(intl, proposedEventTypes) ?? runsManually);
  } else if (currentReady) {
    const proposedName = typeof input.name === 'string' ? input.name : undefined;
    const proposedInstructions =
      typeof input.instructions === 'string' ? input.instructions : undefined;
    const touchSchedule = isScheduleRoutineTool(toolName) || proposedSpec !== undefined;
    const touchTriggers =
      isEventTriggerRoutineTool(toolName) || Array.isArray(input.event_types);

    if (
      proposedName !== undefined &&
      proposedName.trim() !== (workflowQuery.data?.name ?? '').trim()
    ) {
      diffs.push({
        key: 'name',
        before: workflowQuery.data?.name || noneLabel,
        after: proposedName.trim() || noneLabel,
      });
    }
    if (
      proposedInstructions !== undefined &&
      proposedInstructions !== (workflowQuery.data?.instructions ?? '')
    ) {
      diffs.push({
        key: 'instructions',
        before: workflowQuery.data?.instructions || noneLabel,
        after: proposedInstructions || noneLabel,
      });
    }
    if (touchSchedule && cadenceKey(proposedSpec) !== cadenceKey(currentSpec)) {
      diffs.push({
        key: 'schedule',
        before: currentSpec ? formatRoutineScheduleLabel(intl, currentSpec) : noneLabel,
        after: proposedSpec ? formatRoutineScheduleLabel(intl, proposedSpec) : noneLabel,
      });
    }
    if (touchTriggers && proposedEventTypes.join('|') !== currentEventTypes.join('|')) {
      diffs.push({
        key: 'triggers',
        before: formatTriggers(currentEventTypes),
        after: formatTriggers(proposedEventTypes),
      });
    }

    summary = buildUpdateSummary(intl, diffs, proposedSpec, proposedEventTypes);
  }

  const canApprove =
    kind === 'create'
      ? Boolean(createFields?.name.trim()) && Boolean(createFields?.instructions.trim())
      : currentReady;

  return (
    <RoutineProposalCard
      kind={kind}
      name={name}
      summary={summary}
      Icon={Icon}
      instructions={kind === 'create' ? createFields?.instructions : undefined}
      diffs={kind === 'update' ? diffs : undefined}
      onApprove={() => submit('approve')}
      onDeny={() => submit('deny')}
      pendingVerdict={pendingVerdict}
      disabled={isBusy}
      approveDisabled={!canApprove}
      data-testid={
        isScheduleRoutineTool(toolName)
          ? `schedule-workflow-approval-${approval.approval_id}`
          : isEventTriggerRoutineTool(toolName)
            ? `event-triggers-approval-${approval.approval_id}`
            : `workflow-approval-${approval.approval_id}`
      }
    />
  );
};

function buildUpdateSummary(
  intl: ReturnType<typeof useIntl>,
  diffs: RoutineProposalDiff[],
  proposedSpec: ScheduleSpec | undefined,
  proposedEventTypes: EventType[]
): string | undefined {
  if (diffs.length === 0) return undefined;
  const parts: string[] = [];
  for (const diff of diffs) {
    if (diff.key === 'name') {
      parts.push(
        intl.formatMessage(
          {
            defaultMessage: 'Renames to {name}',
            id: '43gbiP+iMA',
            description: 'Update-routine summary when the name changes',
          },
          { name: diff.after }
        )
      );
    } else if (diff.key === 'instructions') {
      parts.push(
        intl.formatMessage({
          defaultMessage: 'updates the instructions',
          id: 'zj0SXpWfcW',
          description: 'Update-routine summary when instructions change',
        })
      );
    } else if (diff.key === 'schedule') {
      parts.push(
        proposedSpec
          ? formatRoutineScheduleSentence(intl, proposedSpec)
          : intl.formatMessage({
              defaultMessage: 'Removes the schedule',
              id: 'SDaJYYWcuu',
              description: 'Update-routine summary when a schedule is cleared',
            })
      );
    } else if (diff.key === 'triggers') {
      const triggerSummary = formatEventTriggerSummary(intl, proposedEventTypes);
      parts.push(
        triggerSummary ??
          intl.formatMessage({
            defaultMessage: 'Removes all event triggers',
            id: 'b6+VrH9dqY',
            description: 'Update-routine summary when event triggers are cleared',
          })
      );
    }
  }
  if (parts.length === 0) return undefined;
  const [first, ...rest] = parts;
  if (!first) return undefined;
  if (rest.length === 0) return first;
  const joined = [first, ...rest].join(', ');
  return joined.charAt(0).toUpperCase() + joined.slice(1);
}
